export const MemoizationFnBuilder = <T extends (...args: any[]) => any>(fn: T, expirationTime: number = 0) => {
  const cache = new Map<string, { value: ReturnType<T>; expirationTime: number }>();
  return (...args: Parameters<T>): ReturnType<T> => {
    const key = JSON.stringify(args);
    const cached = cache.get(key);
    if (cached && cached.expirationTime > Date.now()) {
      return JSON.parse(JSON.stringify(cached.value));
    }
    const value = fn(...args);
    cache.set(key, { value: JSON.parse(JSON.stringify(value)), expirationTime: Date.now() + expirationTime });
    return JSON.parse(JSON.stringify(value));
  };
};

export const MemoizationPromiseFnBuilder = <T extends Array<any>, U>(
  fnToCall: (...args: T) => Promise<U>,
  expirationTime: number = 0,
  context?: any,
) => {
  const cache = new Map<string, { value: U; expirationTime: number }>();
  return async (...args: T): Promise<U> => {
    const key = JSON.stringify(args);
    const cached = cache.get(key);
    if (cached && cached.expirationTime > Date.now()) {
      return Promise.resolve(JSON.parse(JSON.stringify(cached.value)));
    }
    const value = context ? await fnToCall.apply(context, args) : await fnToCall(...args);
    cache.set(key, { value: JSON.parse(JSON.stringify(value)), expirationTime: Date.now() + expirationTime });
    return JSON.parse(JSON.stringify(value));
  };
};

/**
 * Builds a memoized Promise-returning function that caches results in localStorage.
 * @param fnToCall The async function to memoize.
 * @param expirationTime Time in ms before a cache entry expires.
 * @param context Optional `this` context for fnToCall.
 * @param storageKeyPrefix Prefix for keys in localStorage.
 */
export const MemoizationPromiseFnBuilderWithLocalStorage = <T extends any[], U>(
  fnToCall: (...args: T) => Promise<U>,
  expirationTime: number = 0,
  context?: any,
  storageKeyPrefix: string = 'memo'
) => {
  return async (...args: T): Promise<U> => {
    const key = `${storageKeyPrefix}:${JSON.stringify(args)}`;
    const item = localStorage.getItem(key);
    if (item) {
      try {
        const { value, expiration } = JSON.parse(item) as { value: U; expiration: number };
        if (expiration > Date.now()) {
          // Return cached clone
          return JSON.parse(JSON.stringify(value));
        } else {
          localStorage.removeItem(key);
        }
      } catch {
        // ignore parse errors
      }
    }
    // Fetch fresh value
    const result = context
      ? await fnToCall.apply(context, args)
      : await fnToCall(...args);
    try {
      const entry = JSON.stringify({ value: result, expiration: Date.now() + expirationTime });
      localStorage.setItem(key, entry);
    } catch {
      // ignore storage errors (quota, etc.)
    }
    // Return fresh clone
    return JSON.parse(JSON.stringify(result));
  };
};
